import { useEffect } from 'react';
import { getCLS, getFID, getFCP, getLCP, getTTFB, Metric } from 'web-vitals';

const shouldLogPerf =
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_ENABLE_PERF_LOGS === 'true';

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

const logPerformance = (metric: Metric) => {
  if (shouldLogPerf) {
    console.log(`Performance metric: ${metric.name}`, {
      value: metric.value,
      rating: (metric as any).rating || 'unknown'
    });
  }

  if (process.env.NODE_ENV === 'production') {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', metric.name, {
        event_category: 'Web Vitals',
        value: Math.round(metric.value),
        custom_parameter_1: (metric as any).rating || 'unknown',
        non_interaction: true
      });
    }
  }
};

export const PerformanceMonitor = () => {
  useEffect(() => {
    getCLS(logPerformance);
    getFID(logPerformance);
    getFCP(logPerformance);
    getLCP(logPerformance);
    getTTFB(logPerformance);

    if (typeof PerformanceObserver === 'undefined') {
      return;
    }

    const observer = new PerformanceObserver((list) => {
      if (!shouldLogPerf) {
        return;
      }

      list.getEntries().forEach((entry) => {
        if (entry.entryType === 'navigation') {
          const navEntry = entry as PerformanceNavigationTiming;
          const timings = {
            domContentLoaded: navEntry.domContentLoadedEventEnd - navEntry.domContentLoadedEventStart,
            loadComplete: navEntry.loadEventEnd - navEntry.loadEventStart,
            firstByte: navEntry.responseStart - navEntry.requestStart,
            domInteractive: navEntry.domInteractive - navEntry.fetchStart
          };

          console.log('Navigation Timings:', timings);
        }

        if (entry.entryType === 'paint') {
          const paintEntry = entry as PerformancePaintTiming;
          console.log(`${paintEntry.name}: ${paintEntry.startTime.toFixed(2)}ms`);
        }
      });
    });

    observer.observe({ entryTypes: ['navigation', 'paint'] });

    return () => {
      observer.disconnect();
    };
  }, []);

  return null;
};

export const useComponentPerformance = (componentName: string) => {
  const renderStartTime = now();

  useEffect(() => {
    if (!shouldLogPerf) {
      return;
    }

    const renderTime = now() - renderStartTime;
    if (renderTime > 120) {
      console.warn(`Slow component render: ${componentName} took ${renderTime.toFixed(2)}ms`);
    }
  });
};
